import React, { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../api/config";

/**
 * Welle 109: Dokumentenpaket eines Einzugs/Auszugs.
 * Je Soll-Dokument: erzeugt · versandt · unterschrieben zurück (per QR-Code oder Upload),
 * dazu Erzeugen/Öffnen, Versandmappe (Anschreiben + ausgewählte Dokumente) und
 * „als versandt markieren". Oben der verknüpfte Vorgang (Auszug ↔ Einzug).
 *
 * Props: vorgang (detail aus Tab6), onSchreiben(typ), onProtokoll(), onOeffneVorgang(id),
 *        onGeaendert(), meldung(text), fehler(text)
 */
const API = `${API_BASE}/mietverwaltung/api`;
const SCHREIBEN_TYP = { kuendigung_vermieter: "kuendigung", bestaetigung_mieterkuendigung: "bestaetigung",
  kautionsabrechnung: "kautionsabrechnung" };
const ZUSTELLARTEN = [["post", "Post"], ["mail", "E-Mail"], ["persoenlich", "persönlich übergeben"],
  ["einwurf_einschreiben", "Einwurf-Einschreiben"], ["bote", "Bote"]];

const fmt = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join(".") : "");
// kurz für die Chips: 28.09.26
const kurz = (s) => (s ? fmt(s).replace(/\.(\d{2})(\d{2})$/, ".$2") : "");
const ZUSTELL_LABEL = Object.fromEntries(ZUSTELLARTEN);

function Chip({ an, text, farbe }) {
  const stil = an
    ? { background: farbe || "#14532d", color: "#dcfce7", border: "1px solid transparent" }
    : { background: "transparent", color: "#64748b", border: "1px dashed #475569" };
  return <span style={{ ...stil, borderRadius: 999, padding: "1px 8px", fontSize: 11.5, whiteSpace: "nowrap",
    display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "middle" }}>{text}</span>;
}

async function pdfOeffnen(res) {
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Fehler ${res.status}`);
  const url = URL.createObjectURL(await res.blob());
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return res;
}

export default function DokumentenPaket({ vorgang, onSchreiben, onProtokoll, onOeffneVorgang, onGeaendert, meldung, fehler }) {
  const [paket, setPaket] = useState(null);
  const [auswahl, setAuswahl] = useState({});          // dokument_id → true
  const [versand, setVersand] = useState({ zustellart: "post", datum: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState("");

  const laden = useCallback(async () => {
    try {
      const r = await fetch(`${API}/vorgang/${vorgang.id}/paket`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Paket nicht ladbar");
      setPaket(j);
    } catch (e) { fehler?.(String(e.message || e)); }
  }, [vorgang.id, fehler]);
  useEffect(() => { laden(); }, [laden, vorgang.dokumente]);

  const neu = async () => { await laden(); onGeaendert?.(); };

  const erzeugen = async (z) => {
    setBusy(z.typ);
    try {
      if (z.erzeugung === "protokoll") { onProtokoll?.(); return; }
      if (z.erzeugung === "schreiben") { onSchreiben?.(SCHREIBEN_TYP[z.typ]); return; }
      if (z.erzeugung === "mietvertrag") {
        const r = await pdfOeffnen(await fetch(`${API}/vertrag/${encodeURIComponent(vorgang.mietvertrag_nr)}/mietvertrag?ablegen=1`, { method: "POST" }));
        meldung?.(`Mietvertrag erzeugt · Dokumenten-Key ${r.headers.get("X-Dokument-Key") || "–"}`);
      }
      if (z.erzeugung === "bescheinigung") {
        const dokument = z.typ === "wohnungsgeber" ? "wohnungsgeber" : "mietbescheinigung";
        const r = await pdfOeffnen(await fetch(`${API}/bescheinigung/pdf?ablegen=1&kuendigung_id=${vorgang.id}`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mietvertrag_nr: vorgang.mietvertrag_nr, dokument, felder: {} }) }));
        meldung?.(`${z.label} erzeugt · Dokumenten-Key ${r.headers.get("X-Dokument-Key") || "–"}`);
      }
      await neu();
    } catch (e) { fehler?.(String(e.message || e)); }
    finally { setBusy(""); }
  };

  const oeffnen = async (id) => {
    try { await pdfOeffnen(await fetch(`${API}/kuendigung/dokument/${id}/download`)); }
    catch (e) { fehler?.(String(e.message || e)); }
  };

  const gewaehlt = Object.keys(auswahl).filter((k) => auswahl[k]).map(Number);

  const versandmappe = async () => {
    setBusy("mappe");
    try {
      const r = await pdfOeffnen(await fetch(`${API}/vorgang/${vorgang.id}/versandmappe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dokument_ids: gewaehlt, anschreiben: true }) }));
      const aid = Number(r.headers.get("X-Anschreiben-Id") || 0);
      if (aid) setAuswahl((a) => ({ ...a, [aid]: true }));
      meldung?.("Versandmappe erstellt (Anschreiben + ausgewählte Dokumente). Nach dem Versand „als versandt markieren“.");
      await neu();
    } catch (e) { fehler?.(String(e.message || e)); }
    finally { setBusy(""); }
  };

  const alsVersandt = async () => {
    setBusy("versandt");
    try {
      const r = await fetch(`${API}/vorgang/${vorgang.id}/versendet`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dokument_ids: gewaehlt, zustellart: versand.zustellart, datum: versand.datum }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Speichern fehlgeschlagen");
      meldung?.(`${j.markiert} Dokument(e) als versandt markiert (${fmt(versand.datum)}).`);
      setAuswahl({});
      await neu();
    } catch (e) { fehler?.(String(e.message || e)); }
    finally { setBusy(""); }
  };

  if (!paket) return <p className="vg-leer-text">Dokumentenpaket lädt…</p>;
  const p = paket.partner;
  return (
    <div>
      {p && (
        <div className="vg-okbox" style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
          <span>🔗 Verknüpft: <b>{p.art === "einzug" ? "Einzug" : "Auszug"}</b> {p.mv_kurzname || ""} ({p.mietvertrag_nr})
            {p.uebergabe_termin ? ` · Übergabe ${fmt(p.uebergabe_termin)}` : p.kuendigung_zum ? ` · Ende ${fmt(p.kuendigung_zum)}` : ""}
            {p.abgeschlossen_am ? " · abgeschlossen" : ""}</span>
          <button className="me-btn me-btn-outline" style={{ marginLeft: "auto" }} onClick={() => onOeffneVorgang?.(p.id)}>öffnen</button>
        </div>
      )}
      <table className="me-table" style={{ fontSize: 13, width: "100%", tableLayout: "fixed" }}>
        <colgroup><col style={{ width: 28 }} /><col /><col style={{ width: 96 }} /><col style={{ width: 112 }} /><col style={{ width: 124 }} /></colgroup>
        <thead>
          <tr><th></th><th>Dokument</th><th>erzeugt</th><th>versandt</th><th>unterschrieben</th></tr>
        </thead>
        <tbody>
          {paket.zeilen.map((z) => (
            <tr key={z.typ}>
              <td>{z.dokument_id && (
                <input type="checkbox" checked={!!auswahl[z.dokument_id]}
                  onChange={(e) => setAuswahl((a) => ({ ...a, [z.dokument_id]: e.target.checked }))} />
              )}</td>
              <td>
                <b>{z.label}</b>
                {z.doc_key && <div style={{ fontSize: 11, color: "#94a3b8" }}>Dok {z.doc_key}{z.anzahl > 1 ? ` · ${z.anzahl}× erzeugt` : ""}</div>}
                <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                  {z.erzeugung !== "versandmappe" && (
                    <button className="me-btn me-btn-outline" style={{ padding: "1px 7px", fontSize: 11.5 }} disabled={!!busy}
                      onClick={() => erzeugen(z)}>{busy === z.typ ? "…" : z.dokument_id ? "neu erzeugen" : "erzeugen"}</button>
                  )}
                  {z.dokument_id && (
                    <button className="me-btn me-btn-outline" style={{ padding: "1px 7px", fontSize: 11.5 }}
                      onClick={() => oeffnen(z.dokument_id)}>öffnen</button>
                  )}
                  {z.rueck_dokument_id && (
                    <button className="me-btn me-btn-outline" style={{ padding: "1px 7px", fontSize: 11.5 }}
                      onClick={() => oeffnen(z.rueck_dokument_id)}>unterschriebenes öffnen</button>
                  )}
                  {z.erzeugung === "versandmappe" && !z.dokument_id && (
                    <span style={{ fontSize: 11.5, color: "#94a3b8" }}>entsteht mit der Versandmappe</span>
                  )}
                </div>
              </td>
              <td><Chip an={!!z.erzeugt_am} text={z.erzeugt_am ? kurz(z.erzeugt_am) : "offen"} farbe="#1e3a8a" /></td>
              <td><Chip an={!!z.versendet_am} farbe="#7c2d12" text={z.versendet_am ? kurz(z.versendet_am) : "offen"} />
                {z.versendet_am && z.zustellart && (
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{ZUSTELL_LABEL[z.zustellart] || z.zustellart}</div>
                )}</td>
              <td>{z.braucht_unterschrift
                ? <Chip an={!!z.unterschrieben_am} text={z.unterschrieben_am ? `✓ ${kurz(z.unterschrieben_am)}` : "fehlt"} />
                : <span style={{ fontSize: 11.5, color: "#64748b" }}>nicht nötig</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="vg-inline" style={{ marginTop: 8, flexWrap: "wrap" }}>
        <button className="me-btn me-btn-primary" disabled={!!busy || (!gewaehlt.length && paket.art !== "einzug")}
          title="Anschreiben (Textbausteine „Neuer Mieter“) + ausgewählte Dokumente als ein PDF"
          onClick={versandmappe}>{busy === "mappe" ? "Erstellt…" : `Versandmappe (${gewaehlt.length} + Anschreiben)`}</button>
        <select className="vg-in" value={versand.zustellart} onChange={(e) => setVersand({ ...versand, zustellart: e.target.value })}>
          {ZUSTELLARTEN.map(([w, l]) => <option key={w} value={w}>{l}</option>)}
        </select>
        <input type="date" className="vg-in" value={versand.datum} onChange={(e) => setVersand({ ...versand, datum: e.target.value })} />
        <button className="me-btn me-btn-outline" disabled={!!busy || !gewaehlt.length} onClick={alsVersandt}>
          {busy === "versandt" ? "…" : `${gewaehlt.length} als versandt markieren`}
        </button>
      </div>
      <small className="vg-hinweis" style={{ display: "block", marginTop: 6 }}>
        Jedes erzeugte Dokument trägt einen QR-Code. Kommt es unterschrieben als Scan zurück (Rename-me-Batch oder
        Upload unten), wird es automatisch zugeordnet, benannt, abgelegt und der Schritt abgehakt.
      </small>
    </div>
  );
}
